import { Directive, output, signal } from '@angular/core';

export interface DragReorderEvent {
  fromIndex: number;
  toIndex: number;
}

/**
 * Container directive for keyboard/pointer drag-and-drop reordering.
 *
 * Place on the list container alongside `appDragItem` on each child item.
 * Owns all shared drag state and emits `itemReordered` when a drop resolves
 * to a new position, so the host component only needs to reorder its array.
 *
 * @example
 * ```html
 * <ul appDragList (itemReordered)="onReorder($event)">
 *   @for (item of items(); track item.id; let i = $index) {
 *     <li appDragItem [dragItemIndex]="i">...</li>
 *   }
 * </ul>
 * ```
 */
@Directive({
  selector: '[appDragList]',
  standalone: true,
  exportAs: 'appDragList',
})
export class DragListDirective {
  /** Index of the item currently being dragged. */
  readonly dragIndex = signal<number | null>(null);

  /** Index of the item the dragged item is hovering over. */
  readonly dragOverIndex = signal<number | null>(null);

  /** Whether the cursor is in the top or bottom half of the hovered item. */
  readonly dropPosition = signal<'before' | 'after' | null>(null);

  /** Emits when a drag gesture resolves to a valid new position. */
  readonly itemReordered = output<DragReorderEvent>();

  onDragStart(event: DragEvent, index: number): void {
    this.dragIndex.set(index);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    const dragIdx = this.dragIndex();
    if (dragIdx === null || dragIdx === index) {
      this.dragOverIndex.set(null);
      this.dropPosition.set(null);
      return;
    }

    // Determine whether cursor is in the top or bottom half of the target item
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const position = event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';

    this.dragOverIndex.set(index);
    this.dropPosition.set(position);
  }

  onDragLeave(event: DragEvent): void {
    // Ignore leave events triggered by entering a child element
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      this.dragOverIndex.set(null);
      this.dropPosition.set(null);
    }
  }

  onDrop(event: DragEvent, targetIndex: number): void {
    event.preventDefault();
    const fromIndex = this.dragIndex();
    if (fromIndex === null || fromIndex === targetIndex) {
      this.clearDragState();
      return;
    }

    let toIndex = targetIndex;

    if (this.dropPosition() === 'after') {
      toIndex = targetIndex + 1;
    }
    // Account for the removal of the source item when dragging downward
    if (fromIndex < toIndex) {
      toIndex--;
    }

    if (fromIndex !== toIndex) {
      this.itemReordered.emit({ fromIndex, toIndex });
    }

    this.clearDragState();
  }

  onDragEnd(): void {
    this.clearDragState();
  }

  clearDragState(): void {
    this.dragIndex.set(null);
    this.dragOverIndex.set(null);
    this.dropPosition.set(null);
  }
}
